import React from 'react';

import { TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  PropertyStateArgsProps,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
  SpatialSortField,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import {
  formatBoolYesNoUnknown,
  formatList,
  formatNumber,
  formatPercent,
  NULL_VALUE,
} from '@hcs/utils';

const FIELD = PropertyStateFields.basement;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Basement';
const labelShort = 'Basement';
const propertyStatePath: PropertyStatePaths = '/propertyDetails/basement';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  } else if (propertyStateType === 'Core') {
    return propertyState?.propertyDetails?.basement;
  }
  return undefined;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) => {
  const basement = getValue(propertyStateArgs);
  if (!basement) {
    return NULL_VALUE;
  }
  const results = [];
  if (basement.has != null) {
    results.push(formatBoolYesNoUnknown(basement.has));
    if (basement.description) {
      results.push(`description: ${formatList(basement.description)}`);
    }
    if (basement.totalArea) {
      results.push(`sqft total: ${formatNumber(basement.totalArea)}`);
    }
    if (basement.finishedPct) {
      results.push(
        `sqft finished percent: ${formatPercent(basement.finishedPct)}`,
      );
    }
    return results.length > 0 ? results.join('. ') : NULL_VALUE;
  } else {
    return NULL_VALUE;
  }
};

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) => {
  const value = getValue(propertyStateArgs);
  if (value === null) {
    return `Unknown ${labelShort}`;
  } else if (value) {
    return `Has ${labelShort}`;
  } else {
    return `No ${labelShort}`;
  }
};

// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};
// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

export const BASEMENT_CONFIG: PropertyStateFieldConfig<typeof FIELD> = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatInline,
  HeaderCell,
  ContentCell,
  Display,
  spatialSortField: SpatialSortField.Basement,
};
